import { useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';
import { IconButton } from '../Button/Button';
import styles from './Dialog.module.scss';

export interface DialogProps {
  open: boolean;
  title: ReactNode;
  closeLabel: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

/** Модальное окно на нативном <dialog>: фокус, Esc и затемнение — от браузера. */
export function Dialog({ open, title, closeLabel, onClose, children, footer, wide }: DialogProps): ReactElement | null {
  const ref = useRef<HTMLDialogElement>(null);
  // Свой id: диалог подтверждения открывается поверх другого диалога.
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className={`${styles.dialog} ${wide ? styles.wide : ''}`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      aria-labelledby={titleId}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <IconButton icon="close" label={closeLabel} onClick={onClose} />
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </dialog>
  );
}
